import React, { useState, useEffect } from 'react';
import { Music, Calendar, Tag, Search, Play, Pause, User, X, Filter } from 'lucide-react';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import TrackDetailModal from '../components/TrackDetailModal';
import ArtistDetailModal from '../components/ArtistDetailModal';
import PresignedImage from '../components/PresignedImage';
import '../styles/library-page.css';

const Submissions = () => {
  const { user } = useAuth();
  const { playTrack, currentTrack, isPlaying, playPause } = useMusicPlayer();
  const [submissions, setSubmissions] = useState([]);
  const [artistSubmissions, setArtistSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState('tracks');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [showArtistModal, setShowArtistModal] = useState(false);
  // const [isFetching, setIsFetching] = useState(false);



  const fetchSubmissions = React.useCallback(async (retryCount = 0) => {
    try {
      // Admin users get all submissions, regular users get only their own
      const trackEndpoint = user?.role === 'admin' ? '/api/submissions/admin/all' : '/api/submissions';
      const artistEndpoint = '/api/artists/submissions';
      
      console.log('🔍 Fetching submissions from endpoints:', { trackEndpoint, artistEndpoint });
      console.log('🔍 User role:', user?.role);
      console.log('🔍 User ID:', user?.id);
      console.log('🔍 Retry attempt:', retryCount);
      
      const [trackResponse, artistResponse] = await Promise.all([
        api.get(trackEndpoint),
        api.get(artistEndpoint)
      ]);
      
      console.log('✅ Track API response:', trackResponse.data);
      console.log('✅ Artist API response:', artistResponse.data);
      console.log('📊 Track submissions count:', trackResponse.data.submissions?.length || 0);
      console.log('📊 Artist submissions count:', artistResponse.data.submissions?.length || 0);
      
      setSubmissions(trackResponse.data.submissions);
      setArtistSubmissions(artistResponse.data.submissions);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('❌ Failed to fetch submissions:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.response?.status === 429 && retryCount < 3) {
        // Rate limited - wait and retry with exponential backoff
        const waitTime = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`⏳ Rate limited, waiting ${waitTime}ms before retry ${retryCount + 1}`);
        
        setTimeout(() => {
          fetchSubmissions(retryCount + 1);
        }, waitTime);
        
        setError(`Rate limited. Retrying in ${waitTime / 1000} seconds... (Attempt ${retryCount + 1}/3)`);
        return;
      } else if (error.response?.status === 429) {
        setError('Too many requests. Please wait a few minutes and try again.');
      } else {
        setError('Failed to fetch submissions. Please try again.');
      }
    } finally {
      if (retryCount === 0) {
        setLoading(false);
      }
    }
  }, [user]);


  useEffect(() => {
    if (user) {
      fetchSubmissions();
    }
  }, [user, fetchSubmissions]);

  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = submission.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         submission.genre?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenre = !filterGenre || submission.genre === filterGenre;
    const matchesStatus = !filterStatus || submission.status === filterStatus;
    
    return matchesSearch && matchesGenre && matchesStatus;
  });

  const filteredArtistSubmissions = artistSubmissions.filter(submission => {
    const matchesSearch = submission.artist_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         submission.artist_legal_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || submission.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending':
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRowClick = (item, type) => {
    if (type === 'track') {
      setSelectedTrack(item);
      setShowTrackModal(true);
    } else {
      setSelectedArtist(item);
      setShowArtistModal(true);
    }
  };

  const closeTrackModal = () => {
    setShowTrackModal(false);
    setSelectedTrack(null);
  };

  const closeArtistModal = () => {
    setShowArtistModal(false);
    setSelectedArtist(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-50"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="library-page">
        <div className="library-page-header p-6">
          <h1 className="text-2xl font-bold text-white mb-4">
            {user?.role === 'admin' ? 'All Submissions' : 'My Submissions'}
          </h1>
        </div>
        <div className="library-page-scroll">
          <div className="library-page-panel p-12 text-center">
            <h3 className="text-lg font-medium text-white mb-2">Error Loading Submissions</h3>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null);
                fetchSubmissions();
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold-50 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-50 transition-all duration-200 hover:text-black"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activeCount = activeTab === 'tracks' ? filteredSubmissions.length : filteredArtistSubmissions.length;
  const totalCount = activeTab === 'tracks' ? submissions.length : artistSubmissions.length;

  return (
    <div className="library-page">
      <div className="library-page-header relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full" />
        <div className="relative z-10 space-y-5 p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {user?.role === 'admin' ? 'All Submissions' : 'My Submissions'}
                </h1>
                {user?.role !== 'admin' && (
                  <p className="text-sm text-gray-400 mt-1">
                    View your submitted tracks and artists
                  </p>
                )}
              </div>
            </div>
            <div className="text-sm text-white whitespace-nowrap">
              {submissions.length + artistSubmissions.length} total
            </div>
          </div>

          <nav className="flex space-x-8 border-b border-white/10">
            <button
              onClick={() => setActiveTab('tracks')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors -mb-px ${
                activeTab === 'tracks'
                  ? 'border-gold-50 text-gold-50'
                  : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
              }`}
            >
              Tracks ({submissions.length})
            </button>
            <button
              onClick={() => setActiveTab('artists')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors -mb-px ${
                activeTab === 'artists'
                  ? 'border-gold-50 text-gold-50'
                  : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
              }`}
            >
              Artists ({artistSubmissions.length})
            </button>
          </nav>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={activeTab === 'tracks' ? 'Search by title or genre...' : 'Search by artist name...'}
                className={`pl-10 w-full border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm placeholder-gray-500 text-sm py-2 ${searchTerm ? 'pr-10' : 'pr-4'}`}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-0.5"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {activeTab === 'tracks' && (
              <select
                value={filterGenre}
                onChange={(e) => setFilterGenre(e.target.value)}
                className="px-4 py-2 border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm text-sm"
              >
                <option value="">All Genres</option>
                {[...new Set(submissions.map((s) => s.genre).filter(Boolean))].map((genre) => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            )}

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <div className="text-gray-400 flex items-center text-sm md:col-span-1">
              <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
              {activeCount} of {totalCount} {activeTab === 'tracks' ? 'tracks' : 'artists'}
            </div>
          </div>
        </div>
      </div>

      <div className="library-page-scroll">
        {activeTab === 'tracks' ? (
          filteredSubmissions.length === 0 ? (
            <div className="library-page-panel p-12 text-center">
              <Music className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-400">
                {user?.role === 'admin' ? 'No track submissions found' : 'No track submissions yet'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {user?.role === 'admin' ? 'Try adjusting your filters' : 'Submit your first track to get started'}
              </p>
              {user?.role !== 'admin' && (
                <div className="mt-4">
                  <a
                    href="/upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold-50 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-50 transition-all duration-200"
                  >
                    Upload Track
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="library-page-panel overflow-x-auto">
              <table className="library-page-table min-w-full divide-y divide-white/10">
                <thead>
                  <tr>
                    <th />
                    <th>Track</th>
                    {user?.role === 'admin' && <th>User</th>}
                    <th>Genre</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Uploaded</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredSubmissions.map((submission) => (
                    <tr 
                      key={submission.id} 
                      className="hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => handleRowClick(submission, 'track')}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {/* Play/Pause Button - Now in first column */}
                        {submission.url && (
                          currentTrack && currentTrack.id === submission.id && isPlaying ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                playPause();
                              }}
                              className="p-2 bg-gold-50 hover:bg-gold-100 text-white rounded-full transition-colors flex-shrink-0"
                              title="Pause track"
                            >
                              <Pause className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                playTrack(submission, filteredSubmissions);
                              }}
                              className="p-2 text-gold-50 hover:text-gold-100 hover:bg-gold-50/20 rounded-full transition-colors flex-shrink-0"
                              title="Play track"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white">
                            {submission.title}
                          </div>
                          {submission.release_package && (
                            <div className="text-sm text-gray-400">
                              {submission.release_package}
                            </div>
                          )}
                        </div>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-white">
                              {submission.user_email || 'Unknown User'}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Tag className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-white">
                            {submission.genre || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(submission.status)}`}>
                          {submission.status || 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {submission.duration || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-white">
                            {formatDate(submission.created_at)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : filteredArtistSubmissions.length === 0 ? (
          <div className="library-page-panel p-12 text-center">
            <User className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-400">
              {user?.role === 'admin' ? 'No artist submissions found' : 'No artist submissions yet'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {user?.role === 'admin' ? 'Try adjusting your filters' : 'Submit your first artist to get started'}
            </p>
            {user?.role !== 'admin' && (
              <div className="mt-4">
                <a
                  href="/create-artist"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold-50 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-50 transition-all duration-200 hover:text-black"
                >
                  Create New Artist
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="library-page-panel overflow-x-auto">
            <table className="library-page-table min-w-full divide-y divide-white/10">
              <thead>
                <tr>
                  <th />
                  <th>Artist</th>
                  <th>Legal Name</th>
                  {user?.role === 'admin' && <th>User</th>}
                  <th>Bio</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                  {filteredArtistSubmissions.map((submission) => (
                    <tr 
                      key={submission.id} 
                      className="hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => handleRowClick(submission, 'artist')}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          {submission.image ? (
                            <div className="h-8 w-8 rounded-lg overflow-hidden flex-shrink-0">
                              <PresignedImage
                                assetKey={submission.image}
                                assetType="artist"
                                status={submission.status || 'pending'}
                                alt={`${submission.artist_name || 'Artist'} thumbnail`}
                                className="h-8 w-8 object-cover"
                              />
                            </div>
                          ) : (
                            <div className="h-8 w-8 rounded-lg bg-gray-700 flex items-center flex-shrink-0">
                              <User className="h-4 w-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white">
                            {submission.artist_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {submission.artist_legal_name}
                        </div>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-white">
                              {submission.user_email || 'Unknown User'}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="max-w-xs">
                          <div className="text-sm text-white line-clamp-2">
                            {submission.bio || 'No bio provided'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(submission.status)}`}>
                          {submission.status_display || submission.status || 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-white">
                            {formatDate(submission.created_at)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}
      </div>

      <TrackDetailModal
        track={selectedTrack}
        isOpen={showTrackModal}
        onClose={closeTrackModal}
        user={user}
      />

      <ArtistDetailModal
        artist={selectedArtist}
        isOpen={showArtistModal}
        onClose={closeArtistModal}
        user={user}
      />
    </div>
  );
};

export default Submissions; 